import React from "react";
import { Download, Eraser, History, MoreHorizontal, NotebookPen, Undo2 } from "lucide-react";
import DispatchHeaderActionMenu, { DISPATCH_MENU_ACTION_CLASS_NAME as ITEM } from "../resultsPageChrome/DispatchHeaderActionMenu.jsx";
import DispatchFillCheckbox from "./DispatchFillCheckbox.jsx";
import DispatchGanttPreview from "./DispatchGanttPreview.jsx";
import ValidationHistory from "./ValidationHistory.jsx";
import { truckNumber } from "../../utils/consolidationPresentation.js";
import { SECONDARY } from "./Fields.jsx";

const PANEL_TITLES = { gantt: "Gantt du Dispatch", notes: "Notes", history: "Historique des validations" };

// The menu and its confirmations stay mounted when the menu closes.
// geotabWait : raison pour laquelle le comparatif Geotab n'est pas encore vérifié (action grisée).
// onReset : « Annuler les modifications » (brouillon modifiable), grisé tant que dirty est faux.
// canFill : « Saisir la consolidation » et un droit Geotab ; canEmpty : « Saisir la consolidation ».
export default function SheetActions({ row, fill, canFill, canEmpty, canClear, geotabWait = "", onReset = null, dirty = false, onPanel, menuRef }) {
  const restoreFocus = () => menuRef.current?.querySelector('[aria-haspopup="menu"]')?.focus();
  return <span ref={menuRef} className="inline-flex items-center gap-1">
    <DispatchHeaderActionMenu label="Actions du relevé" icon={MoreHorizontal} iconOnly contextKey={row.source_key || row.id}
      triggerClassName={`${SECONDARY} !min-h-7 !h-7 !w-8 !p-0 [&>svg:last-child]:hidden`} menuClassName="!w-72">
      {canFill && row.status === "draft" ? <button type="button" role="menuitem" className={ITEM} disabled={!canClear || fill.busy || Boolean(geotabWait)}
        title={geotabWait || undefined} onClick={() => fill.toggle(true)}><Download />Remplir depuis Geotab</button> : null}
      <button type="button" role="menuitem" className={ITEM} onClick={() => onPanel("notes")}><NotebookPen />Notes{row.notes ? " · renseignées" : ""}</button>
      <button type="button" role="menuitem" className={ITEM} disabled={!row.id} onClick={() => onPanel("history")}><History />Historique des validations</button>
      {onReset ? <button type="button" role="menuitem" className={ITEM} disabled={!dirty} onClick={onReset}><Undo2 />Annuler les modifications</button> : null}
      {row.status === "draft" && canEmpty ? <button type="button" role="menuitem" className={ITEM} data-destructive disabled={!canClear} onClick={() => fill.toggle(false)}><Eraser />Vider les saisies</button> : null}
    </DispatchHeaderActionMenu>
    {canEmpty ? <DispatchFillCheckbox hideControl fill={fill} disabled={!canClear} onRestoreFocus={restoreFocus}
      label={`Camion ${truckNumber(row)} · ${row.dispatch_period === "evening" ? "Soir" : "Jour"}`} /> : null}
    {fill.missing > 0 ? <span role="status" className="text-xs text-amber-800">{fill.missing} valeur(s) indisponible(s)</span> : null}
  </span>;
}

export function SheetPanel({ panel, row, notes, onClose }) {
  const region = React.useRef(null);
  const previousPanel = React.useRef(panel);
  const [ganttOpened, setGanttOpened] = React.useState(panel === "gantt");
  React.useEffect(() => {
    if (panel === "gantt") setGanttOpened(true);
    // Display checkboxes keep their popover open and retain the focus.
    if (panel && panel !== "gantt" && panel !== previousPanel.current) {
      region.current?.scrollIntoView({ block: "nearest" });
      region.current?.querySelector("button")?.focus({ preventScroll: true });
    }
    previousPanel.current = panel;
  }, [panel]);
  // Keep an opened Gantt mounted to preserve its memoized reference while editing.
  return <section ref={region} hidden={!panel} aria-label={PANEL_TITLES[panel]} className="min-w-0 rounded-[4px] border border-slate-200 bg-white p-2">
    {panel ? <header className="flex items-center justify-between gap-2"><h3 className="text-sm font-semibold">{PANEL_TITLES[panel]}</h3>
      <button type="button" className={SECONDARY} onClick={onClose}>Fermer</button></header> : null}
    {panel === "notes" ? notes : null}
    {panel === "history" ? <ValidationHistory expanded kind="sheet" ownerId={row.id} revision={row.revision} timezone={row.timezone} /> : null}
    {ganttOpened || panel === "gantt" ? <div hidden={panel !== "gantt"} className="mt-2 min-w-0"><DispatchGanttPreview assignment={row.dispatch_assignment}
      dispatchDate={row.dispatch_date} dispatchPeriod={row.dispatch_period} referenceKey={row.reference_fingerprint || row.source_version} /></div> : null}
  </section>;
}
